import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthParam = () => {
  const token = localStorage.getItem('c3i_token');
  return token ? `Bearer ${token}` : '';
};

export default function CambiaPasswordPage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
      toast.error('Tutti i campi sono obbligatori');
      return;
    }
    
    if (formData.new_password.length < 8) {
      toast.error('La nuova password deve essere di almeno 8 caratteri');
      return;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (formData.current_password === formData.new_password) {
      toast.error('La nuova password deve essere diversa dalla password attuale');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password?Authorization=${encodeURIComponent(getAuthParam())}`,
        {
          current_password: formData.current_password,
          new_password: formData.new_password
        }
      );
      
      setSuccess(true);
      toast.success('Password modificata con successo');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il cambio password');
    } finally {
      setSubmitting(false);
    }
  };

  // Success page
  if (success) {
    return (
      <div className="p-6 lg:p-8" data-testid="cambio-password-success">
        <div className="max-w-md mx-auto">
          <Card className="card-institutional">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                Password modificata
              </h2>
              <p className="text-slate-600 mb-6">
                La tua password è stata modificata con successo.
              </p>
              <Button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alla Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="cambio-password-page">
      {/* Header */}
      <div className="page-header -mx-6 lg:-mx-8 -mt-6 lg:-mt-8 mb-8 px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-4">Cambia Password</h1>
        <p className="text-slate-500 mt-1">Modifica la tua password di accesso al portale</p>
      </div>

      <div className="max-w-md mx-auto">
        <Card className="card-institutional">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-slate-600" />
              Nuova Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="current_password">Password attuale *</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={formData.current_password}
                    onChange={(e) => handleChange('current_password', e.target.value)}
                    placeholder="Inserisci la password attuale"
                    data-testid="input-current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="new_password">Nuova password *</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={formData.new_password}
                    onChange={(e) => handleChange('new_password', e.target.value)}
                    placeholder="Inserisci la nuova password"
                    data-testid="input-new-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Minimo 8 caratteri</p>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Conferma nuova password *</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={(e) => handleChange('confirm_password', e.target.value)}
                    placeholder="Ripeti la nuova password"
                    data-testid="input-confirm-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting}
                data-testid="submit-change-password-btn"
              >
                {submitting ? 'Modifica in corso...' : 'Cambia Password'}
              </Button>

              <p className="text-xs text-slate-500 text-center">* Campi obbligatori</p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
